import React from 'react';
import { Button } from 'rebass/styled-components';

import { Box, Flex, Text } from 'shared/components/display';
import PromptButton from 'shared/components/molecules/PromptButton';
import { BUTTON_VARIANTS } from 'shared/styles/button';

/**
 * Interim guard against firing hundreds of individual stamp/reject requests at once
 * (bulk actions dispatch one request per id today). Removed once a true bulk endpoint lands.
 */
export const MAX_BULK_SELECTION = 100;

interface ApprovalToolbarProps {
  /** Bulk actions only render for approvers; superadmin-only viewers see nothing here. */
  canStamp: boolean;
  selectedCount: number;
  totalCount: number;
  onSelectAll: () => void;
  onSelectNone: () => void;
  onBulkApprove: () => void;
  onBulkReject: () => void;
  rejectPromptText: string;
  dataCy: string;
}

/**
 * Bulk-action toolbar shown above each approval table (Select all/none + bulk approve/reject).
 * Extracted from ApprovalDashboard so the page component stays readable and this isn't
 * redefined on every parent render.
 */
const ApprovalToolbar: React.FC<ApprovalToolbarProps> = ({
  canStamp,
  selectedCount,
  totalCount,
  onSelectAll,
  onSelectNone,
  onBulkApprove,
  onBulkReject,
  rejectPromptText,
  dataCy,
}) => {
  if (!canStamp) return null;
  const noSelection = selectedCount === 0;
  const overCap = selectedCount > MAX_BULK_SELECTION;
  return (
    <Flex className="approval-dashboard__toolbar" data-cy={`${dataCy}__toolbar`}>
      <Button
        variant={BUTTON_VARIANTS.TERTIARY}
        onClick={onSelectAll}
        disabled={totalCount === 0}
        type="button"
      >
        Select all
      </Button>
      <Button
        variant={BUTTON_VARIANTS.TERTIARY}
        onClick={onSelectNone}
        disabled={noSelection}
        type="button"
      >
        Select none
      </Button>
      <Box className="approval-dashboard__toolbar-spacer" />
      <Button
        variant={BUTTON_VARIANTS.PRIMARY_SMALL}
        onClick={onBulkApprove}
        disabled={noSelection || overCap}
        type="button"
      >
        {noSelection ? 'Approve selected' : `Approve ${selectedCount} selected`}
      </Button>
      <PromptButton
        variant={BUTTON_VARIANTS.SECONDARY_SMALL}
        disabled={noSelection || overCap}
        text={rejectPromptText}
        confirmButtonText="Reject"
        onConfirm={onBulkReject}
      >
        {noSelection ? 'Reject selected' : `Reject ${selectedCount} selected`}
      </PromptButton>
      {overCap && (
        <Text className="approval-dashboard__toolbar-cap-note">
          {`Select up to ${MAX_BULK_SELECTION} at a time`}
        </Text>
      )}
    </Flex>
  );
};

export default ApprovalToolbar;
